import React from 'react';
import usePage from '@dashboard/hooks/usePage';
import { BarChart3, Eye, Hourglass, RefreshCw, Ticket, TrendingUp, UserCheck } from 'lucide-react';
import Link from '@dashboard/components/Link';
import * as KPICard from '../components/KPICard';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import getConversionRate from '@dashboard/services/getConversionRate';
import EventTicketSales from '../components/EventTicketSales';
import { useQuery } from '@tanstack/react-query';
import { eventDashboardOverviewKey } from '@/dashboard/lib/queryKeys';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import Spinner from '@/dashboard/components/Spinner';
import PageOverviewHeader from '../components/PageOverviewHeader';
import { KPITrend, moneyFormatter, TopReferrers } from '../components/PageCharts';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import formatRecoveredStats from '@/dashboard/services/abandonment/formatRecoveredStats';
import getRecoveredTooltipText from '@/dashboard/services/abandonment/getRecoveredTooltipText';
import { usePageSummary } from '../components/PageSummaryRange';

export function GuestDetailsCard({ page, guestDetails }) {
  const { added, tickets, missingBookings } = guestDetails;
  return (
    <KPICard.Container>
      <KPICard.Icon icon={UserCheck} />
      <KPICard.Header>
        <KPICard.Title>Attendee details</KPICard.Title>
        <KPICard.InfoTooltip>Attendees added out of tickets booked</KPICard.InfoTooltip>
      </KPICard.Header>
      <KPICard.Value>
        {added}/{tickets}
      </KPICard.Value>
      {missingBookings ? (
        <Link to={`/pages/${page._id}/bookings?attendees=missing`} className="text-sm font-normal">
          {missingBookings} booking{missingBookings === 1 ? '' : 's'} missing attendee details
        </Link>
      ) : null}
    </KPICard.Container>
  );
}

export function OutstandingCard({ amount, currency }) {
  return (
    <KPICard.Container>
      <KPICard.Icon icon={Hourglass} />
      <KPICard.Header>
        <KPICard.Title>Outstanding</KPICard.Title>
        <KPICard.InfoTooltip>
          Outstanding revenue on unpaid and partially paid bookings.
        </KPICard.InfoTooltip>
      </KPICard.Header>
      <KPICard.Value>{getPriceLabel({ amount, currency })}</KPICard.Value>
    </KPICard.Container>
  );
}

export default function CheckoutOverview() {
  const { page } = usePage();
  const { seller } = useAuth();
  const { eventDetails } = page;
  const { getEventOverview } = useSellerQueries();

  const { summary, isLoading: summaryLoading, isAllTime } = usePageSummary();
  const totals = summary?.totals;
  const conversionRate = getConversionRate(totals?.conversions, totals?.views);

  const { data: eventDashboardOverviewData, isPending: eventDashboardOverviewIsLoading } = useQuery(
    {
      queryKey: eventDashboardOverviewKey(page._id),
      queryFn: () => getEventOverview({ sellerId: page.seller?._id, pageId: page._id }),
      enabled: page.seller?._id != null && page._id != null,
    },
  );

  return (
    <div>
      <PageOverviewHeader
        page={page}
        side={
          <>
            {totals?.outstanding > 0 && (
              <OutstandingCard amount={totals.outstanding} currency={eventDetails?.currency} />
            )}

            <KPICard.Container>
              <KPICard.Icon icon={TrendingUp} />
              <KPICard.Header>
                <KPICard.Title>Conversion rate</KPICard.Title>
                <KPICard.InfoTooltip>
                  Bookings ÷ page views. Counts bookings you later archived or canceled.
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {summaryLoading ? <Spinner size="small" /> : `${conversionRate}%`}
              </KPICard.Value>
            </KPICard.Container>

            <KPICard.Container>
              <KPICard.Icon icon={RefreshCw} />
              <KPICard.Header>
                <KPICard.Title>Recovered</KPICard.Title>
                <KPICard.InfoTooltip>
                  {getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {!summaryLoading ? (
                  formatRecoveredStats({
                    totalRecovered: summary?.recovered?.totalRecovered,
                    totalAbandoned: summary?.recovered?.totalAbandoned,
                    totalRecoveredRevenue: summary?.recovered?.totalRecoveredRevenue,
                    currency: page.eventDetails?.currency,
                    showRevenue: seller.checkoutAbandonment?.showRecovered,
                  })
                ) : (
                  <Spinner size="small" />
                )}
              </KPICard.Value>
            </KPICard.Container>
            <TopReferrers />
            {eventDashboardOverviewData?.guestDetails ? (
              <GuestDetailsCard
                page={page}
                guestDetails={eventDashboardOverviewData.guestDetails}
              />
            ) : null}
          </>
        }
      >
        <KPICard.Container>
          <KPICard.Icon icon={BarChart3} />
          <KPICard.Header>
            <KPICard.Title>Revenue</KPICard.Title>
            <KPICard.InfoTooltip>
              Gross revenue from paid, pending, unpaid and refunded bookings, before refunds and
              Stripe fees. Includes archived and canceled bookings.
            </KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {!summaryLoading ? (
              <>
                {getPriceLabel({
                  amount: totals?.revenue,
                  currency: eventDetails?.currency,
                })}
              </>
            ) : (
              <Spinner size="small" />
            )}
          </KPICard.Value>
          <KPITrend metric="revenue" label="Revenue" formatValue={moneyFormatter(page)} />
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={Ticket} />
          <KPICard.Header>
            <KPICard.Title>Tickets sold</KPICard.Title>
            <KPICard.InfoTooltip>
              Number of valid tickets. On All time, out of the event's capacity.
            </KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {/* Out of capacity only for all time: capacity is for the whole event. */}
            {isAllTime && !eventDashboardOverviewIsLoading ? (
              <>
                {eventDashboardOverviewData?.totalTicketsSold}
                {eventDashboardOverviewData?.totalCapacity != null &&
                  `/${eventDashboardOverviewData?.totalCapacity}`}
              </>
            ) : !isAllTime && !summaryLoading ? (
              (totals?.tickets ?? 0)
            ) : (
              <Spinner size="small" />
            )}
          </KPICard.Value>
          <KPITrend metric="tickets" label="Tickets sold" />
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={Eye} />
          <KPICard.Header>
            <KPICard.Title>Page views</KPICard.Title>
            <KPICard.InfoTooltip>Total number of event views.</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {summaryLoading ? <Spinner size="small" /> : (totals?.views ?? 0)}
          </KPICard.Value>
          <KPITrend metric="views" label="Page views" />
        </KPICard.Container>
      </PageOverviewHeader>

      <div className="mt-8 mb-16">
        {eventDashboardOverviewIsLoading ? (
          <Spinner />
        ) : (
          <EventTicketSales ticketGroups={eventDashboardOverviewData?.ticketGroups} page={page} />
        )}
      </div>
    </div>
  );
}
